/**
 * syncQueue — IndexedDB-backed FIFO queue for offline mutations.
 *
 * Phase 2A scope (Feb 2026): ONLY stock-transfer (`products/{id}/move`)
 * mutations are queued. Purchases, invoices, etc. remain online-only
 * until 2A is battle-tested in production.
 *
 * Each queued item:
 *   {
 *     id:           string  // crypto.randomUUID() — used as X-Client-Action-Id
 *     kind:         'product_move' | (future: 'purchase' | 'invoice')
 *     payload:      arbitrary JSON (the mutation args)
 *     enqueued_at:  ISO string
 *     attempts:     int (incremented on each replay attempt)
 *     last_error:   string|null
 *     status:       'pending' | 'in_flight' | 'failed'
 *   }
 *
 * Storage: a single `idb-keyval` slot at `tsp:syncQueue` holding the
 * full array. The queue should stay tiny (<50 items) in practice
 * because stock moves are infrequent and reconnect-sync drains it
 * quickly, so we don't bother with a full IDB object store.
 */
import { createStore, get as idbGet, set as idbSet } from 'idb-keyval';

const QUEUE_STORE = createStore('tsp-sync-queue', 'kv');
const KEY = 'tsp:syncQueue';

/** Cheap UUIDv4 — `crypto.randomUUID()` when available, fallback otherwise. */
export function newActionId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (_) { /* ignore */ }
  // RFC4122-ish fallback for old WebViews
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

async function readAll() {
  try {
    const v = await idbGet(KEY, QUEUE_STORE);
    return Array.isArray(v) ? v : [];
  } catch (_) {
    // IDB blocked (private browsing) or quota error — treat as empty
    // queue rather than crashing the caller. The user simply loses any
    // previously-queued offline writes from this device, which is the
    // expected behavior for an unsupported storage environment.
    return [];
  }
}

async function writeAll(items) {
  try {
    await idbSet(KEY, items, QUEUE_STORE);
  } catch (e) {
    console.warn('[syncQueue] write failed', e?.message);
  }
}

export async function getQueue() {
  return readAll();
}

export async function getPendingCount() {
  const q = await readAll();
  return q.filter((i) => i.status !== 'failed').length;
}

export async function enqueue({ kind, payload }) {
  const items = await readAll();
  const item = {
    id: newActionId(),
    kind,
    payload,
    enqueued_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
    status: 'pending',
  };
  items.push(item);
  await writeAll(items);
  return item;
}

export async function markInFlight(id) {
  const items = await readAll();
  const idx = items.findIndex((i) => i.id === id);
  if (idx < 0) return;
  items[idx].status = 'in_flight';
  items[idx].attempts += 1;
  await writeAll(items);
}

export async function markPending(id) {
  const items = await readAll();
  const idx = items.findIndex((i) => i.id === id);
  if (idx < 0) return;
  items[idx].status = 'pending';
  await writeAll(items);
}

export async function markFailed(id, errMsg) {
  const items = await readAll();
  const idx = items.findIndex((i) => i.id === id);
  if (idx < 0) return;
  items[idx].status = 'failed';
  items[idx].last_error = String(errMsg || 'Unknown error').slice(0, 200);
  await writeAll(items);
}

export async function remove(id) {
  const items = await readAll();
  await writeAll(items.filter((i) => i.id !== id));
}

export async function retryAllFailed() {
  const items = await readAll();
  for (const it of items) {
    if (it.status === 'failed') {
      it.status = 'pending';
      it.last_error = null;
    }
  }
  await writeAll(items);
}

export async function clearQueue() {
  await writeAll([]);
}
